import { CUSTOMER_ID, KYC_WEB_DATA_CACHE, isClient } from "@adgm/libs";
import { EKycStep } from "@adgm/cross-models";

/**
 * KYC类型枚举
 */
export enum EKycType {
  INDIVIDUAL = "individual",
  INSTITUTION = "institution",
}

/**
 * KYC表单数据结构
 */
export interface IKycFormData {
  // 通用字段
  type?: number | undefined;
  nationality?: string;

  // 个人KYC字段
  individualResidenceList?: any[];
  individualPIList?: any[];

  // 机构KYC字段
  institutionFileUrl?: any[];
  institutionFileList?: any[];
  directorBirthDate?: any;
  beneficiaryBirthDate?: any;
  companyRepresentativeBirthDate?: any;

  // 问卷相关字段
  [key: string]: any; // 允许其他字段
}

/**
 * KYC表单本地存储数据结构
 */
export interface IKycLocalStorage<T = IKycFormData> {
  formData: T; // kyc缓存的数据，平铺在formData里面，前端需要用的时候直接拿或者从中提取
  [EKycType.INDIVIDUAL]: EKycStep; // 个人kyc认证到第几步
  [EKycType.INSTITUTION]: EKycStep; // 机构kyc认证到第几步
}

/**
 * KYC Web存储管理器
 */
export class KycWebStorage<T = IKycFormData> {
  // 初始化的KYC存储数据结构
  private readonly initKycStorageData: IKycLocalStorage<T> = {
    formData: {} as T,
    [EKycType.INDIVIDUAL]: EKycStep.QUESTIONNAIRE,
    [EKycType.INSTITUTION]: EKycStep.QUESTIONNAIRE,
  };

  /**
   * 获取存储键
   */
  private getStorageKey(): string {
    const customerId = isClient ? sessionStorage.getItem(CUSTOMER_ID) || "" : "";
    return `${customerId}_${KYC_WEB_DATA_CACHE}`;
  }

  /**
   * 从本地存储中加载数据
   */
  loadFromLocalStorage(): IKycLocalStorage<T> {
    if (!isClient) return this.initKycStorageData;

    try {
      const dataStr = localStorage.getItem(this.getStorageKey());
      if (!dataStr) return this.initKycStorageData;

      const data = JSON.parse(dataStr);
      return data as IKycLocalStorage<T>;
    } catch (error) {
      console.error("Failed to load KYC data from storage:", error);
      return this.initKycStorageData;
    }
  }

  /**
   * 保存KYC表单数据
   */
  saveToLocalStorage({
    formData,
    isIndividual,
    currentStep,
  }: {
    formData?: T;
    isIndividual: boolean;
    currentStep?: EKycStep;
  }): void {
    const storageData = this.loadFromLocalStorage();

    // 更新表单数据
    if (formData) {
      storageData.formData = formData;
    }
    if (currentStep) {
      storageData[isIndividual ? EKycType.INDIVIDUAL : EKycType.INSTITUTION] = currentStep;
    }

    try {
      const dataStr = JSON.stringify(storageData);
      localStorage.setItem(this.getStorageKey(), dataStr);
    } catch (error) {
      console.error("Failed to save KYC data to storage:", error);
    }
  }
}

// 导出默认实例
export const kycWebStorage = new KycWebStorage();
